import type { MobilePreferences, ReminderMode, ReminderRule } from '../types';
import { Toggle } from './Ui';

export type ReminderDraft = Omit<ReminderRule, 'id' | 'sourceType' | 'sourceId' | '_sourceKey' | 'updatedAt'>;

export function defaultReminderDraft(preferences: MobilePreferences, sourceType: string): ReminderDraft {
  return {
    mode: preferences.defaultReminderMode || 'once',
    leadMinutes: sourceType === 'task' ? preferences.defaultLeadMinutes || 15 : Math.max(60, preferences.defaultLeadMinutes || 60),
    repeatMinutes: preferences.defaultRepeatMinutes || 60,
    dailyTime: preferences.defaultDailyTime || '08:30',
    continueOverdue: true,
    inApp: true,
    desktop: true,
    mobilePush: true,
    centre: true,
    dashboard: sourceType === 'important',
    sound: preferences.soundEnabled !== false,
  };
}

export function ReminderEditor({ value, onChange }: { value: ReminderDraft; onChange: (value: ReminderDraft) => void }) {
  const patch = (next: Partial<ReminderDraft>) => onChange({ ...value, ...next });
  const repeatPreset = [15, 30, 60, 120, 240, 720, 1440].includes(Number(value.repeatMinutes)) ? String(value.repeatMinutes) : 'custom';
  return <details className="reminder-editor" open={value.mode !== 'none'}>
    <summary>Notifications</summary>
    <div className="reminder-body">
      <div className="form-grid two">
        <label><span>Reminder</span><select value={value.mode} onChange={event => patch({ mode: event.target.value as ReminderMode })}>
          <option value="none">None</option><option value="once">Once</option><option value="daily">Daily</option><option value="repeat">Repeating</option><option value="persistent">Persistent until completed</option>
        </select></label>
        {value.mode !== 'daily' && value.mode !== 'none' && <label><span>Before due</span><select value={value.leadMinutes} onChange={event => patch({ leadMinutes: Number(event.target.value) })}>
          <option value={0}>At time</option><option value={15}>15 minutes</option><option value={30}>30 minutes</option><option value={60}>1 hour</option><option value={180}>3 hours</option><option value={1440}>1 day</option><option value={4320}>3 days</option><option value={10080}>7 days</option>
        </select></label>}
        {value.mode === 'daily' && <label><span>Daily time</span><input type="time" value={value.dailyTime} onChange={event => patch({ dailyTime: event.target.value })}/></label>}
        {(value.mode === 'repeat' || value.mode === 'persistent') && <>
          <label><span>Repeat interval</span><select value={repeatPreset} onChange={event => {
            const next = event.target.value;
            if (next !== 'custom') patch({ repeatMinutes: Number(next) });
            else if ([15, 30, 60, 120, 240, 720, 1440].includes(value.repeatMinutes)) patch({ repeatMinutes: 90 });
          }}>
            <option value="15">15 minutes</option><option value="30">30 minutes</option><option value="60">1 hour</option><option value="120">2 hours</option><option value="240">4 hours</option><option value="720">12 hours</option><option value="1440">1 day</option><option value="custom">Custom interval</option>
          </select></label>
          {repeatPreset === 'custom' && <label><span>Custom minutes</span><input type="number" min="15" step="5" value={value.repeatMinutes} onChange={event => patch({ repeatMinutes: Math.max(15, Number(event.target.value) || 15) })}/></label>}
        </>}
      </div>
      {value.mode !== 'none' && <div className="notification-options">
        <Toggle checked={value.inApp} onChange={checked => patch({ inApp: checked })} label="MD Glass in-app"/>
        <Toggle checked={value.mobilePush !== false} onChange={checked => patch({ mobilePush: checked })} label="iPhone Web Push"/>
        <Toggle checked={value.desktop} onChange={checked => patch({ desktop: checked })} label="Windows notification" detail="Synchronised with desktop"/>
        <Toggle checked={value.centre} onChange={checked => patch({ centre: checked })} label="Activity Centre"/>
        <Toggle checked={value.sound} onChange={checked => patch({ sound: checked })} label="Tabletop bell"/>
        <Toggle checked={value.continueOverdue} onChange={checked => patch({ continueOverdue: checked })} label="Continue while overdue"/>
      </div>}
    </div>
  </details>;
}
